import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from "@angular/router";
import { Observable, catchError, map, of } from "rxjs";

import { SnackbarService } from "../components/snackbar/snackbar.service";
import { talkerCanAccessFormations, talkerCanAccessResources } from "../tool-functions/talker-plan-access";
import { Talker } from "../../users/models/talker.entity";
import { Role } from "../../users/models/users.entity";
import { ProfileService } from "../../users/services/profile.service";

type TalkerPlanAccess = "formations" | "resources";

@Injectable({
  providedIn: "root",
})
export class TalkerPlanAccessGuard implements CanActivate, CanActivateChild {
  constructor(
    private readonly profileService: ProfileService,
    private readonly router: Router,
    private readonly snackbarService: SnackbarService,
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> {
    return this.checkAccess(route);
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> {
    return this.checkAccess(childRoute.parent ?? childRoute);
  }

  private checkAccess(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    const requestedAccess = route.data?.["talkerPlanAccess"] as TalkerPlanAccess | undefined;
    if (!requestedAccess) {
      return of(true);
    }

    return this.profileService.loadProfile().pipe(
      map((profile) => {
        if (profile.role !== Role.Talker) {
          return true;
        }

        const talker = profile as Talker;
        const isAllowed =
          requestedAccess === "formations"
            ? talkerCanAccessFormations(talker)
            : talkerCanAccessResources(talker);

        if (isAllowed) {
          return true;
        }

        this.snackbarService.pushMessage(
          requestedAccess === "formations"
            ? "Les formations ne sont pas incluses dans votre offre."
            : "Les ressources ne sont disponibles qu'une fois inscrit a un parcours.",
          "error",
        );

        return this.router.createUrlTree(["home"]);
      }),
      catchError(() => of(this.router.createUrlTree(["home"]))),
    );
  }
}
